import { useMedia } from "react-use";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/v2-components/ui/dialog";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/v2-components/ui/sheet";
import { Button } from "@/v2-components/ui/button";
import CrossIcon from "@/v2-components/core/icon/cross-icon";
import { useTrans } from "@/utils/useTrans";
import { AuthUtils } from "@/utils/AuthUtils";
import { useAuthPopup } from "@/hooks/useAuthPopup";
import { useAuthEmbed } from "@/hooks/useAuthEmbed";
import { useRouter } from "next/router";
import { User } from "@/models/User";

/**
 * 결제 진입 선택 모달 (#5293).
 *
 * 시안: 데스크톱 [29435:37715](Login Modal / Desktop — B. Social) ·
 *       모바일 [29449:37771](Login Modal / Mobile — A. Social) = **바텀 드로어**.
 *
 * `Checkout` 을 눌렀는데 로그인이 안 돼 있으면 **로그인/게스트를 여기서 고르게** 한다.
 * 종전에는 버튼을 둘로 갈라(`Guest Checkout` / `Account Checkout`, develop #5101) 표현했는데,
 * 그러면 CTA 행에 버튼이 3개가 되어 시안의 `Checkout` 1개 구조가 성립하지 않는다.
 *
 * ## 시안의 폼 자리는 **auth 화면 그 자체**다 (데스크톱)
 *
 * 시안은 이메일 입력 · 비밀번호 입력 · 소셜 3종이 모달 안에 한 번에 보인다. 비밀번호는
 * 폐지됐고(auth Phase 2), 나머지는 **폼을 복제하지 않고** auth `/login?mode=embed` 를
 * iframe 으로 그 자리에 그려서 만족시킨다 — 인증 표면이 auth 한 곳으로 유지된다
 * (관리 포인트 하나 · 세션 리코더가 내부를 못 읽음).
 *
 * 껍데기·순서·게스트 경로는 시안 그대로다(제목 · 카드 제목 · `or` 구분선 ·
 * `Continue as Guest`). 교체 지점은 **`AuthInline` 하나**로 유지된다.
 *
 * ## 모바일은 아직 리다이렉트다
 *
 * 그 경로는 **페이지가 떠나므로** `onSignIn` 의 "담기 선행" 우회가 여전히 필요하다.
 * 데스크톱만 임베드로 옮겼다 — 상세는 `docs/checkout-auth-embed-policy.md`.
 */
export function CheckoutChoiceDialog({
  open,
  onClose,
  onSignIn,
  onAuthenticated,
  onGuest,
}: {
  /**
   * ⚠️ 호출처가 결제 잠금(`isDisabled`)을 **의도 상태에서** 되돌린다 —
   * `PdpCheckoutButtons` 의 `useEffect` 주석 참고(파생식으로 표시만 끄면 잠금이 풀릴 때
   * 사용자가 누르지 않은 모달이 다시 뜬다). 여기서 잠금을 다시 계산하지 않는다.
   */
  open: boolean;
  onClose: () => void;
  /**
   * 로그인을 거쳐 결제. 지금 구현은 **고른 구성을 장바구니에 먼저 담고** auth 로 보낸다 —
   * PDP 옵션 스토어(`useBuyTactSuit`)는 persist 가 꺼져 있어 로그인 왕복에 선택이 날아가고,
   * 장바구니 스토어는 persist 라서 담아두면 구성이 유지된다(`handleAccountCheckout` 주석).
   */
  onSignIn: () => void;
  /**
   * 팝업 로그인이 완료된 뒤 실행. **게스트와 같은 결제 경로**를 태운다 — 선택과 집행이
   * 같은 식을 쓰게 해서 "로그인했는데 게스트 주문" 같은 갈림을 만들지 않는다. 회원 여부는
   * 결제 실행 시점의 토큰 해석(`resolveCheckoutToken`)이 결정한다.
   */
  onAuthenticated: (user: User) => void;
  onGuest: () => void;
}) {
  const { t } = useTrans(["gnb"]);
  // 형제 `retailer-sheet.tsx` 와 **같은 브레이크포인트**를 쓴다(`laptop` = 1024px).
  // 두 곳이 갈리면 같은 화면 폭에서 한쪽은 시트, 한쪽은 다이얼로그가 된다.
  const isMobile = useMedia("(max-width: 1023px)", false);

  const title = t("Choose how to check out");

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
        {/* 시안 29449:37773 — `pt-12 pb-24 px-16` · `gap-20` · `rounded-t-16`.
            `side="bottom"` 의 기본 `h-[90%]` 를 **`h-auto` 로 덮는다** — 시안 드로어는
            내용 높이만큼이고(360×~430), 90% 로 두면 빈 공간이 절반 남는다.

            ⚠️ **`flex flex-col` 을 반드시 같이 준다.** `sheetVariants` 에는 display 가 없어
            기본이 `block` 이고, 그러면 `gap-5` 가 **적용되지 않는다**(gap 은 flex/grid 전용).
            실측으로 걸린 방식이 중요하다 — `getComputedStyle().rowGap` 은 `display:block`
            에서도 **20px 을 그대로 돌려준다**. 그래서 "gap=20px" 을 확인하고 통과시켰는데
            실기에서는 `또는` 구분선과 아래 버튼이 붙어 있었다. 간격을 잴 때는 gap 값이
            아니라 **display 를 같이** 봐야 한다(또는 형제 간 실제 거리를 재야 한다). */}
        <SheetContent
          side="bottom"
          aria-describedby={undefined}
          className="flex h-auto flex-col gap-5 rounded-t-2xl px-4 pb-6 pt-3"
        >
          {/* 그래버. 시안 29449:37775 = 40×4 · `Cool Gray/20` · r2 */}
          <div className="flex justify-center">
            <span className="h-1 w-10 rounded-sm bg-coolgray-20" />
          </div>
          {/* ⚠️ `SheetHeader` 기본값에서 **셋을 덮어야 한다**:
              · `-mt-4 laptop:-mt-8` — **음수 마진**이라 padding 만 덮으면 위 그래버와 겹친다
                (memory: sheetheader-negative-margin) → `mt-0 laptop:mt-0`
              · `py-5 laptop:py-7` → `py-0 laptop:py-0` (간격은 시트의 `gap-5` 가 준다)
              · ★`text-center sm:text-left` — 모바일에서 **제목만 가운데 정렬**된다.
                아래 카드 제목·버튼은 왼쪽이라 좌우가 어긋나 보인다(실기에서 잡혔다) →
                `text-left` 로 덮는다. 세 개 중 **하나만 덮으면 나머지가 남는다.** */}
          <SheetHeader className="mt-0 items-center justify-between gap-2 py-0 text-left laptop:mt-0 laptop:py-0">
            {/* 모바일 제목은 **22/28**(`text-2xl`)이다 — 데스크톱 24/32 와 다르다(시안). */}
            <SheetTitle className="min-w-0 flex-1 text-2xl font-400 text-coolgray-100">
              {title}
            </SheetTitle>
            {/* `SheetClose` 는 `SheetPrimitive.Close` 그대로라 **아이콘을 안 그린다**
                (`DialogClose` 와 다르다). 시안 29449:37778 의 24px 닫기를 직접 그린다. */}
            <SheetClose
              aria-label={t("Close")}
              className="shrink-0 focus:outline-none"
            >
              <CrossIcon />
            </SheetClose>
          </SheetHeader>
          <ChoiceBody
            isMobile
            onSignIn={onSignIn}
            onAuthenticated={onAuthenticated}
            onGuest={onGuest}
          />
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      {/* 시안 29435:37715 — `p-32` · `gap-28` · `rounded-8` · 폭 480 · 그림자 0/8/16 15% */}
      <DialogContent className="z-[100] flex max-w-[480px] flex-col gap-7 rounded-lg p-8 shadow-[0px_8px_16px_0px_rgba(0,0,0,0.15)]">
        <DialogHeader className="mb-0 items-start">
          {/* 데스크톱 제목은 **24/32**(`text-3xl`). `DialogTitle` 기본이 `text-lg`·`font-500`·
              `coolgray-90` 이므로 셋 다 덮는다 — 하나만 덮으면 나머지가 시안과 어긋난다. */}
          <DialogTitle className="text-3xl font-400 text-coolgray-100">
            {title}
          </DialogTitle>
          {/* `DialogClose` 는 `CrossIcon` 을 그려주고 `absolute right-0` 이 붙는다
              (기준은 `DialogHeader` 의 `relative`). `p-8` 안쪽 오른쪽 끝 = 시안 자리다. */}
          <DialogClose />
        </DialogHeader>
        <ChoiceBody
          isMobile={false}
          onSignIn={onSignIn}
          onAuthenticated={onAuthenticated}
          onGuest={onGuest}
        />
      </DialogContent>
    </Dialog>
  );
}

/**
 * 제목 아래 본문 — 데스크톱·모바일이 **완전히 같다**(시안 29435:37721 = 29449:37779).
 * 한 곳에 둬서 두 표면이 갈리지 않게 한다.
 */
function ChoiceBody({
  isMobile,
  onSignIn,
  onAuthenticated,
  onGuest,
}: {
  /** 시트/다이얼로그를 가른 그 판정을 그대로 내려보낸다 — 훅이 따로 재지 않게 한다. */
  isMobile: boolean;
  onSignIn: () => void;
  onAuthenticated: (user: User) => void;
  onGuest: () => void;
}) {
  const { t } = useTrans(["gnb"]);

  return (
    <>
      {/* Block / Sign in and check out — 시안 `gap-16` */}
      <div className="flex flex-col gap-4">
        {/* 카드 제목 16/22 semibold `tracking-[0.5px]` (시안 Body/Large Semibold) */}
        <p className="text-lg font-600 tracking-[0.5px] text-coolgray-100">
          {t("Sign in and check out")}
        </p>
        <AuthInline
          isMobile={isMobile}
          onSignIn={onSignIn}
          onAuthenticated={onAuthenticated}
        />
      </div>

      {/* `or` 구분선 — 선-텍스트-선 3단, `gap-12`. 텍스트는 `Cool Gray/50`(coolgray-60 이
          아니다 — 시안 #878D96). */}
      <div className="flex items-center gap-3">
        <span className="h-px flex-1 bg-coolgray-20" />
        <span className="text-sm tracking-[0.4px] text-coolgray-50">
          {t("or")}
        </span>
        <span className="h-px flex-1 bg-coolgray-20" />
      </div>

      {/* ★`size="sm"`(40px · 12px) — 시안 29435:37796 이 416×40 이다. `size` 를 생략하면
          기본 `md` 라 `min-h-[54px] text-lg`(16px)가 붙고, 그때 `h-*` 는 `min-h` 에
          덮여 **죽은 클래스**가 된다(실측). 폰트가 커지면 라벨 폭 여유도 같이 줄어든다. */}
      <Button
        size="sm"
        variant="blue-outline"
        shape="pill"
        className="h-10 w-full"
        onClick={onGuest}
      >
        {t("Continue as Guest")}
      </Button>
    </>
  );
}

/**
 * ★교체 지점 — **두 표면 모두** auth 로그인 화면을 iframe 으로 임베드한다.
 *
 * 시안(29435:37715 / 모바일 29449:37771)은 `Checkout` 을 눌렀을 때 이메일 입력·로그인
 * 버튼·SSO 3종이 모달 안에 **한 번에** 보인다. 임베드는 그 구조를 만들면서도 자격증명
 * 폼을 여기 복제하지 않는다 — 화면의 문서 오리진이 그대로 auth 다.
 *
 * ## 모바일이 오히려 이득이 크다
 *
 * 종전 모바일은 전체 리다이렉트라 PDP 옵션 스토어(persist 꺼짐)가 **왕복에 날아가서**
 * "담기 선행" 으로 우회하고 있었다. 임베드는 페이지를 떠나지 않으므로 그 우회가 필요 없다.
 * `onSignIn`(담기 선행 → auth 이동)은 이제 **폴백 전용**이다 — 프레이밍 거부·팝업 차단.
 */
function AuthInline({
  isMobile,
  onSignIn,
  onAuthenticated,
}: {
  /**
   * 시트/다이얼로그 판정. 임베드 자체는 두 표면이 같고, **높이 상한**만 다르다 —
   * 모바일은 소프트 키보드가 올라오면 가용 세로가 크게 줄어든다.
   */
  isMobile: boolean;
  onSignIn: () => void;
  onAuthenticated: (user: User) => void;
}) {
  return (
    <AuthEmbedFrame
      isMobile={isMobile}
      onAuthenticated={onAuthenticated}
      onSignIn={onSignIn}
    />
  );
}

/**
 * auth `/login?mode=embed` 를 모달 안에 그린다.
 *
 * 높이는 iframe 이 postMessage 로 알려준다(크로스 오리진이라 부모가 못 잰다).
 * 로그인 감지·핸드셰이크 재시도·정체 판정은 `useAuthEmbed` 참고.
 */
function AuthEmbedFrame({
  isMobile,
  onAuthenticated,
  onSignIn,
}: {
  isMobile: boolean;
  onAuthenticated: (user: User) => void;
  /** 임베드가 뜨지 않거나 창이 막혔을 때의 탈출로(담기 선행 → auth 이동). */
  onSignIn: () => void;
}) {
  const { t } = useTrans(["gnb"]);
  const { locale } = useRouter();
  const { open: openAuthWindow, isWaiting } = useAuthPopup();

  const openWindow = (provider?: string) =>
    openAuthWindow({
      onAuthenticated,
      onFallback: onSignIn,
      lang: locale,
      provider,
    });

  const { iframeRef, src, height, isStalled } = useAuthEmbed({
    onAuthenticated,
    // 홈페이지 로케일을 그대로 넘긴다 — 두 레포의 로케일 집합이 같다
    // (`en/ko/ja/zh/es/fr/de`). `kr/jp/cn` 은 **번역 폴더명**이라 여기 쓰면 안 된다.
    lang: locale,
    // ★SSO 는 **부모가 연다.** iframe 이 직접 열면 `/popup-complete` 의 opener 가
    //   auth 오리진이라 완료 신호가 버려진다(auth#453). 데스크톱·모바일 공통 —
    //   모바일에서 새 탭이 되더라도, 전체 리다이렉트가 옵션을 **항상** 잃는 것보다 낫다.
    onSsoRequested: openWindow,
  });

  // SSR 에서는 `success-url`(현재 주소)을 만들 수 없어 src 가 빈 문자열이다.
  // 빈 src 로 렌더하면 iframe 이 **현재 페이지를 자기 안에 다시 로드**한다.
  if (!src) return null;

  return (
    <div className="flex flex-col gap-3">
      <iframe
        ref={iframeRef}
        src={src}
        title={t("Sign in and check out")}
        className="w-full border-0"
        style={{
          height,
          // ★상한을 둔다. 브리지 높이를 그대로 쓰면 두 자리에서 화면을 넘는다:
          //   · 모바일에서 **소프트 키보드**가 올라오면 가용 세로가 절반 이하가 된다
          //   · `/sso-consent`(성별·출생연도·거주국·플랫폼·장르·약관 3종)는 원래 길다
          // 넘치는 만큼은 iframe **내부 스크롤**로 처리된다(포커스된 입력은 브라우저가
          // 알아서 스크롤해 올린다). 상한이 없으면 시트가 화면 밖으로 자란다.
          maxHeight: isMobile ? "56vh" : "70vh",
        }}
        // 인증 화면이라 자동 채우기·비밀번호 관리자가 동작해야 한다. `sandbox` 를 걸면
        // allow-* 를 다 열어야 해서 실익이 없고, 오리진이 우리 것이라 격리 이득도 없다.
      />
      {/* ★탈출로 — **3단 폴백의 2·3단**.
          프레이밍이 거부되면(`frame-ancestors` 오설정·auth 장애·확장 프로그램) iframe 은
          **빈 박스로 조용히 실패**하고, 그러면 사용자에게 남는 선택지가
          `Continue as Guest` 뿐이라 **로그인 자체가 불가능해진다.**

          ① 임베드 → ② 별도 창(페이지 유지) → ③ 리다이렉트(창이 막히면 `onFallback`).
          정상 경로에서는 렌더되지 않는다 — 판정은 `useAuthEmbed.isStalled`. */}
      {isStalled && (
        <button
          type="button"
          disabled={isWaiting}
          onClick={() => openWindow()}
          className="text-sm tracking-[0.4px] text-blue-60 underline"
        >
          {isWaiting
            ? t("Continue in the sign-in window")
            : t("Open the sign-in page")}
        </button>
      )}
    </div>
  );
}
